import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './BookingConfirmation.css';
import { FaCheckCircle, FaBed, FaUser, FaRegCreditCard, FaMoneyBillWave, FaCalendarAlt, FaShieldAlt, FaEye, FaHome } from 'react-icons/fa';


const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(location.state);
  const bookingId = data?.bookingId || data?.bookingDetails?.id;

  // Check for fallback data in sessionStorage if no state data
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    if (!data) {
      const storedConfirmation = sessionStorage.getItem('bookingConfirmation');
      if (storedConfirmation) {
        try {
          const confirmationData = JSON.parse(storedConfirmation);
          setData(confirmationData);
          // Clear the stored confirmation data after using it
          sessionStorage.removeItem('bookingConfirmation');
        } catch (error) {
          console.error('Error parsing stored booking confirmation data:', error);
          // If no data available, redirect to home
          navigate('/');
        }
      } else {
        // If no data, redirect to home page
        navigate('/');
      }
    }
    // Here you would call your backend API to send email/SMS confirmation
    // Example: sendConfirmationMail({ ...data, bookingId })
  }, [data, navigate, bookingId]);

  if (!data) return null;

  return (
    <>
      <Header />
      <div className="booking-confirmation-outer">
        <div className="booking-container confirmation-card">
          <div className="step-header">
            <FaCheckCircle className="confirmation-icon" />
            <h2>Booking Confirmed!</h2>
            <p className="booking-id">Booking ID: <strong>{bookingId || 'N/A'}</strong></p>
            {(data?.paymentVerified || data?.bookingDetails?.paymentDetails?.verified) && (
              <div className="payment-verified">
                <FaShieldAlt className="verified-icon" />
                <span>Payment Verified & Secure</span>
              </div>
            )}
          </div>

          <div className="review-content confirmation-details">
            <div className="personal-info-review">
              <h3>Guest Information</h3>
              <div className="info-row"><span>Name:</span><span>{data.formData?.firstName ? `${data.formData.firstName} ${data.formData.lastName || ''}` : (data.bookingDetails?.guestName || 'N/A')}</span></div>
              <div className="info-row"><span>Email:</span><span>{data.formData?.email || data.bookingDetails?.guestEmail || 'N/A'}</span></div>
              <div className="info-row"><span>Phone:</span><span>{data.formData?.phone || data.bookingDetails?.guestPhone || 'N/A'}</span></div>
              {(data.formData?.specialRequests || data.bookingDetails?.formData?.specialRequests) && (
                <div className="info-row"><span>Special Requests:</span><span>{data.formData?.specialRequests || data.bookingDetails?.formData?.specialRequests || ''}</span></div>
              )}
            </div>
            <div className="summary-section">
              <h3>Stay Details</h3>
              <div className="summary-row"><span>Check-in</span><span>{data.bookingData?.start || data.bookingDetails?.checkIn || 'N/A'}</span></div>
              <div className="summary-row"><span>Check-out</span><span>{data.bookingData?.end || data.bookingDetails?.checkOut || 'N/A'}</span></div>
              <div className="summary-row"><span>Duration</span><span>{data.bookingData?.nights || data.bookingDetails?.nights || 1} nights</span></div>
              <div className="summary-row"><span>Number of Beds</span><span>{data.bookingData?.beds || data.bookingDetails?.beds || 1}</span></div>
              <div className="summary-row"><span>Location</span><span>{data.bookingData?.location || data.bookingDetails?.location || 'Sindhi Colony, Hyderabad'}</span></div>
            </div>
            <div className="summary-section">
              <h3>Selected Beds</h3>
              <div className="summary-row">
                <span>Beds</span>
                <span>
                  {data.selectedBeds && data.selectedBeds.length
                    ? data.selectedBeds.map(b => `${b.id || b.number || b.label || 'Bed'} (${b.position || 'N/A'}, Room ${b.roomId || 'N/A'})`).join(', ')
                    : (data.bookingDetails?.selectedBeds && data.bookingDetails.selectedBeds.length
                      ? data.bookingDetails.selectedBeds.map(b => `${b.id || b.number || b.label || 'Bed'} (${b.position || 'N/A'}, Room ${b.roomId || 'N/A'})`).join(', ')
                      : 'Not specified')}
                </span>
              </div>
            </div>
            <div className="summary-section">
              <h3>Payment Method</h3>
              <div className="summary-row"><span>Method</span><span>{data.paymentMethod === 'razorpay' ? 'Razorpay (Online)' : (data.bookingDetails?.paymentMethod === 'razorpay' ? 'Razorpay (Online)' : 'Cash')}</span></div>
              <div className="summary-row"><span>Total</span><span>₹{data.bookingData?.total || data.bookingDetails?.amount || data.bookingDetails?.total || 0}</span></div>
            </div>
          </div>
          <div className="confirmation-success-msg">
            <p>Thank you for booking with Skyline Backpackers!<br />
            A confirmation email and SMS has been sent to your registered contact details.</p>
            
            {data?.saveError && (
              <div className="save-error-notice">
                <p style={{color: '#ff6b6b', fontSize: '14px', marginTop: '10px'}}>
                  Note: There was an issue saving your booking to Firebase, but your payment was successful. 
                  Please contact support with your booking ID for assistance.
                </p>
              </div>
            )}
            
            {data?.firebaseSaved && (
              <div className="firebase-success-notice">
                <p style={{color: '#28a745', fontSize: '14px', marginTop: '10px'}}>
                  ✓ Your booking has been securely saved to our database.
                </p>
              </div>
            )}
            
            {data?.emailSent && (
              <div className="email-success-notice">
                <p style={{color: '#28a745', fontSize: '14px', marginTop: '10px'}}>
                  ✓ Confirmation email sent to {data.formData?.email || data.bookingDetails?.guestEmail}
                </p>
              </div>
            )}
            
            {data?.emailSent === false && (
              <div className="email-error-notice">
                <p style={{color: '#ff6b6b', fontSize: '14px', marginTop: '10px'}}>
                  ⚠ Email notification could not be sent. Please save your booking details.
                </p>
              </div>
            )}
          </div>
          
          <div className="confirmation-actions">
            <button 
              className="btn btn-primary" 
              onClick={() => navigate('/bookings')}
            >
              <FaEye style={{marginRight: '8px'}} />
              View My Bookings
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/')}
            >
              <FaHome style={{marginRight: '8px'}} />
              Back to Home
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BookingConfirmation;
